import React from "react";
import FormButtons from "../../buttons/FormButtons";
import ChangeCheckContext from "../support/ChangeCheckContext";
import useChangeChecker from "../hooks/useChangeChecker";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useAxiosSetup} from "../../Api";
import _ from "lodash";
import {convertFromDate, convertToDate} from "../../../support/dateHandler";
import ProgressBar from "../../dialogs/ProgressBar";
import {useModal, toastSaveSuccessful} from "../../dialogs/ModalContext";

function GenericForm(props) {

    const [data, setData] = React.useState(null);
    const [changeCheckReset, setChangeCheckReset, changed, indicateChange] = useChangeChecker(true)

    const location = useLocation();
    const currentPathname = location.pathname;
    const newPage = currentPathname.includes('new')
    const navigate = useNavigate();
    const [newPathname, setNewPathname] = React.useState("")
    const [shouldNavigate, setShouldNavigate] = React.useState(false)
    const api = useAxiosSetup();

    const contentType = props.contentType ? props.contentType : 'application/json'
    const {id} = useParams()
    const instanceId = props.id !== undefined ? props.id : id;
    const [disable, setDisable] = React.useState(!newPage)
    const [savingInProgress, setSavingInProgress] = React.useState(false)
    const {showModal} = useModal();

    React.useEffect(() => {
        const link = (instanceId === undefined) ? props.baseUrl + 'initial_data/' : props.baseUrl + instanceId + '/get_single/'
        const config = {
            params: props.initalParams
        };
        api.get(link, config).then((response) => {
            loadData(response.data.serialized_data)
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    function loadData(receivedData) {
        const newData = props.parseData ? props.parseData(receivedData, commonParseData) : commonParseData(receivedData)
        setData(newData)
    }

    function commonParseData(newData) {
        if (Array.isArray(newData)) {
            return newData.map(commonParseData);
        }
        if (typeof newData === 'object' && newData !== null) {
            const parsedData = {...newData};
            Object.keys(parsedData).forEach((key) => {
                const value = parsedData[key];
                if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    parsedData[key] = convertToDate(value);
                } else if (typeof value === 'object' && value !== null) {
                    parsedData[key] = commonParseData(value);
                }
            });
            return parsedData;
        }
        return newData;
    }

    function commonParseFromData(newData) {
        if (Array.isArray(newData)) {
            return newData.map(commonParseFromData);
        }
        if (typeof newData === 'object' && newData !== null) {
            const parsedData = {...newData};
            Object.keys(parsedData).forEach((key) => {
                const value = parsedData[key];
                if (value instanceof Date) {
                    parsedData[key] = convertFromDate(value);
                } else if (typeof value === 'object' && value !== null) {
                    parsedData[key] = commonParseFromData(value);
                }
            });
            return parsedData;
        }
        return newData;
    }

    function submit(event) {
        if (props.progressWhenSaving) {
            setSavingInProgress(true)
        }
        event.preventDefault()
        const updated = props.parseFromData ? props.parseFromData(data, commonParseFromData) : commonParseFromData(data)
        let dataToSend = JSON.stringify(updated)
        if (props.customSubmit) {
            dataToSend = props.customSubmit(dataToSend)
        }
        const url = (instanceId === null || instanceId === undefined) ? props.baseUrl + 'add_single/' : props.baseUrl + instanceId + '/update_single/'
        api.post(url, dataToSend, {
            headers: {
                'Content-Type': contentType,
                'Accept': 'application/json'
            }
        }).then((response) => {
            if (newPage) {
                if (props.reloadDataAfterSave) {
                    loadData(response.data.serialized_data)
                }
                setNewPathname(currentPathname.replace('new', response.data.serialized_data.id))
                setShouldNavigate(true)
                setChangeCheckReset(!changeCheckReset)
                toastSaveSuccessful()
            } else {
                // props.parseData(response.data.serialized_data)
                if (props.reloadDataAfterSave) {
                    loadData(response.data.serialized_data)
                }
                setChangeCheckReset(!changeCheckReset)
                toastSaveSuccessful()
            }
            if (props.progressWhenSaving) {
                setSavingInProgress(false)
            }
        }).catch(() => {
            if (props.progressWhenSaving) {
                setSavingInProgress(false)
            }
        })
    }

    React.useEffect(() => {
        /* Need to wait when navigating for changed prop to be set to false in hook. Otherwise, user might be popped with
        dialog to avoid page leave.*/
        if (shouldNavigate && !changed && newPathname !== "") {
            navigate(newPathname)
        }
    }, [changed, shouldNavigate, newPathname]); // eslint-disable-line react-hooks/exhaustive-deps

    function updateData(updates) {
        //find object by path and update it with new value. Updates can be an array of event objects, or a single object.
        // The reason for functional update of state is that it is guaranteed to have the most recent state. Otherwise,
        // nasty endless update loops can occur.
        setData(data => {

                // Deep clone data object
                let clonedData = _.cloneDeep(data);

                if (!Array.isArray(updates)) {
                    updates = [updates];
                }

                for (const update of updates) {
                    const keys = update.target.name.split("."); // ["product", "name"]

                    let value = clonedData;
                    for (const [i, key] of keys.entries()) {
                        if (i === keys.length - 1) {
                            value[key] = update.target.value;
                        } else {
                            value = value[key];
                        }
                    }
                }
                indicateChange(true)
                return clonedData;
            }
        )
    }

    function updateCustom(newData) {
        // This lets forms provide their own way of updating data.
        indicateChange(true)
        setData(newData);
    }

    return (
        data === null ? null : (
            <div>
                <ChangeCheckContext.Provider value={{changeCheckReset: changeCheckReset, disable: disable}}>
                    <form onSubmit={submit}>
                        <FormButtons setDisable={setDisable}
                                     reportGeneration={props.reportGeneration}
                                     reportType={props.reportType}
                                     noReportPreview={props.noReportPreview}
                                     qrGeneration={props.qrGeneration}
                                     priceOffer={props.priceOffer}
                                     priceOfferVersion={props.priceOfferVersion}
                                     newPage={newPage}
                                     nextBtkData={props.nextBtkData}
                                     id={instanceId}
                        />
                        <div className="form-group">
                            <div className="col-sm-offset-2 col-sm-10">
                                <div className="mb-4 mt-4">
                                    <h3>{newPage ? props.labelNew : props.label}</h3>
                                </div>
                                {props.formContent(data, updateData, updateCustom)}
                            </div>
                        </div>
                    </form>
                </ChangeCheckContext.Provider>
                {savingInProgress && <ProgressBar/>}
            </div>
        )
    )
}

export default GenericForm
